import { Component } from '@theme/component';
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { lockScroll, unlockScroll, isClickedOutside } from '@theme/utilities';

/**
 * SD: EDARA Header.
 * - Öffnet/schließt das Menü-Panel (Button, Overlay, Escape)
 * - Sticky-Verhalten ("scroll-up" / "always") mit denselben data-sticky-state-Werten wie Horizon
 * - Hält --header-height und --header-group-height aktuell (werden von Horizon-Sections genutzt)
 *
 * - Öffnet das Client-Service-Popup (<dialog>, Escape schließt es nativ)
 *
 * @typedef {{ toggle: HTMLButtonElement, panel: HTMLElement, serviceDialog?: HTMLDialogElement }} Refs
 * @extends {Component<Refs>}
 */
class SdHeader extends Component {
  requiredRefs = ['toggle', 'panel'];

  /** @type {Element | null} */
  #scrollContainer = null;
  #lastScrollTop = 0;
  /** @type {number | null} */
  #rafId = null;

  #resizeObserver = new ResizeObserver(() => this.#updateHeights());

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.#updateHeights();

    if (this.getAttribute('sticky') === 'scroll-up') this.#bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#rebindScroll);

    document.addEventListener('keydown', this.#onKeydown);

    // Nach dem Absenden des Newsletter-Formulars Panel mit der Meldung wieder öffnen.
    // Nur wenn dieses Formular abgeschickt wurde (der Footer hat ein zweites).
    this.addEventListener('submit', this.#onSubmit);
    const form = this.refs.panel.querySelector('form');
    if (this.refs.panel.querySelector('[data-open-panel]') && consumeSubmitted(form?.id)) this.open();
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    try {
      sessionStorage.setItem(SUBMITTED_KEY, event.target.id);
    } catch {}
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#scrollContainer?.removeEventListener('scroll', this.#onScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.#rebindScroll);
    document.removeEventListener('keydown', this.#onKeydown);
    unlockScroll(this);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  // Solange Panel oder Popup offen sind, scrollt die Seite nicht (Horizons Scroll-Sperre)
  open() {
    this.refs.panel.hidden = false;
    this.setAttribute('open', '');
    this.refs.toggle.setAttribute('aria-expanded', 'true');
    lockScroll(this);
  }

  close() {
    if (!this.isOpen) return;
    this.refs.panel.hidden = true;
    this.removeAttribute('open');
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    unlockScroll(this);
  }

  openService() {
    const dialog = this.refs.serviceDialog;
    if (!dialog) return;
    this.close();
    dialog.showModal();
    lockScroll(dialog);
    // Auch beim Schließen per Escape wieder freigeben
    dialog.addEventListener('close', () => unlockScroll(dialog), { once: true });
  }

  closeService() {
    this.refs.serviceDialog?.close();
  }

  /**
   * Klick auf den abgedunkelten Hintergrund schließt das Popup.
   * Per Klickposition prüfen: Horizons on:click setzt event.target immer auf den Dialog.
   * @param {MouseEvent} event
   */
  onServiceClick(event) {
    const dialog = this.refs.serviceDialog;
    if (dialog && isClickedOutside(event, dialog)) this.closeService();
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    this.close();
    this.refs.toggle.focus();
  };

  #updateHeights() {
    const headerGroup = document.querySelector('#header-group');
    let groupHeight = 0;
    for (const element of headerGroup?.children ?? []) {
      if (element instanceof HTMLElement) groupHeight += element.offsetHeight;
    }
    const barHeight = this.querySelector('.sd-header__bar')?.getBoundingClientRect().height ?? this.offsetHeight;
    document.body.style.setProperty('--header-height', `${Math.round(barHeight)}px`);
    document.body.style.setProperty('--header-group-height', `${Math.round(groupHeight)}px`);
  }

  #bindScroll() {
    this.#scrollContainer = getScrollEventTarget();
    this.#scrollContainer.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#lastScrollTop = getScrollTop();
  }

  #rebindScroll = () => {
    this.#scrollContainer?.removeEventListener('scroll', this.#onScroll);
    if (this.getAttribute('sticky') === 'scroll-up') this.#bindScroll();
  };

  #onScroll = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateStickyState();
    });
  };

  #updateStickyState() {
    const scrollTop = getScrollTop();
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const height = this.offsetHeight;
    this.#lastScrollTop = scrollTop;

    // Bei offenem Panel nichts ändern, sonst springt das Menü weg
    if (this.isOpen) return;

    // inactive = normale Position, active = fixiert und sichtbar, idle = fixiert, aber nach oben weggefahren
    const previous = this.dataset.stickyState;
    let state = previous;
    if (scrollTop <= 0) {
      state = 'inactive';
    } else if (isScrollingUp) {
      if (scrollTop > height || state === 'active') state = 'active';
    } else if (scrollTop > height) {
      state = 'idle';
    }

    if (state === previous) return;

    // Nur zwischen sichtbar und weggefahren animieren. Wird der Header beim ersten Runterscrollen
    // fixiert, ist er schon aus dem Bild und soll nicht sichtbar nach oben fahren.
    this.toggleAttribute('data-sticky-animate', previous === 'active' || state === 'active');
    this.dataset.stickyState = state;
  }
}

const SUBMITTED_KEY = 'sd-newsletter-form';

/**
 * Prüft, ob dieses Formular zuletzt abgeschickt wurde, und löscht den Eintrag (gleiche Logik wie sd-footer.js).
 * @param {string | undefined} id
 */
function consumeSubmitted(id) {
  try {
    if (!id || sessionStorage.getItem(SUBMITTED_KEY) !== id) return false;
    sessionStorage.removeItem(SUBMITTED_KEY);
    return true;
  } catch {
    return false;
  }
}

if (!customElements.get('sd-header')) {
  customElements.define('sd-header', SdHeader);
}
